import { useState, useRef, useEffect } from "react";
import {
  CloseIcon,
  EditIcon,
  CameraIcon,
  AddPersonIcon,
  LinkIcon,
  ExitIcon,
  AdminIcon,
  CopyIcon,
  DeleteIcon,
  SearchIcon,
} from "../../../svg/chat";
import {
  updateGroup,
  addGroupMembers,
  removeGroupMember,
  leaveGroup,
  makeGroupAdmin,
  removeGroupAdmin,
  generateInviteLink,
  revokeInviteLink,
  updateGroupSettings,
} from "../../../functions/chat";
import { getFriendsPageInfos } from "../../../functions/user";
import { uploadImages } from "../../../functions/uploadImages";
import { PulseLoader } from "react-spinners";
import "./style.css";

export default function GroupInfo({ conversation, onClose, onUpdate, user }) {
  const [activeSection, setActiveSection] = useState("info"); // info, members, settings, addMembers
  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [groupName, setGroupName] = useState(conversation?.group?.name || "");
  const [groupDescription, setGroupDescription] = useState(
    conversation?.group?.description || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [inviteLink, setInviteLink] = useState(
    conversation?.group?.inviteLink || ""
  );
  const [friends, setFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [settings, setSettings] = useState(
    conversation?.group?.settings || {
      onlyAdminsCanSend: false,
      onlyAdminsCanEditInfo: false,
      approveNewMembers: false,
    }
  );

  const fileInputRef = useRef(null);

  const isAdmin =
    conversation?.group?.admins?.includes(user?.id) ||
    conversation?.group?.creator === user?.id;

  // Load friends for adding members
  useEffect(() => {
    const loadFriends = async () => {
      if (activeSection === "addMembers") {
        try {
          const result = await getFriendsPageInfos(user.token);
          if (result.status === "ok") {
            // Filter out users already in the group
            const participantIds = conversation?.participants?.map((p) => p._id);
            const availableFriends = (result.data.friends || []).filter(
              (f) => !participantIds.includes(f._id)
            );
            setFriends(availableFriends);
          }
        } catch (err) {
          console.error("Error loading friends:", err);
        }
      }
    };
    loadFriends();
  }, [activeSection, user?.token, conversation?.participants]);

  // Update group name
  const handleUpdateName = async () => {
    if (!groupName.trim() || groupName === conversation?.group?.name) {
      setEditingName(false);
      return;
    }

    setLoading(true);
    try {
      await updateGroup(conversation._id, { name: groupName.trim() }, user.token);
      setSuccess("Nome atualizado");
      setEditingName(false);
      onUpdate && onUpdate({ ...conversation, group: { ...conversation.group, name: groupName.trim() } });
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao atualizar nome");
    } finally {
      setLoading(false);
    }
  };

  // Update group description
  const handleUpdateDescription = async () => {
    if (groupDescription === conversation?.group?.description) {
      setEditingDesc(false);
      return;
    }

    setLoading(true);
    try {
      await updateGroup(
        conversation._id,
        { description: groupDescription },
        user.token
      );
      setSuccess("Descrição atualizada");
      setEditingDesc(false);
      onUpdate && onUpdate({ ...conversation, group: { ...conversation.group, description: groupDescription } });
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao atualizar descrição");
    } finally {
      setLoading(false);
    }
  };

  // Update group picture
  const handlePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("path", "socializa/groups");

      const uploadResult = await uploadImages(formData, user.token);
      const pictureUrl = uploadResult[0]?.url || uploadResult[0];

      await updateGroup(conversation._id, { picture: pictureUrl }, user.token);
      setSuccess("Foto atualizada");
      onUpdate && onUpdate({ ...conversation, group: { ...conversation.group, picture: pictureUrl } });
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao atualizar foto");
    } finally {
      setLoading(false);
    }
  };

  // Generate invite link
  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const result = await generateInviteLink(conversation._id, user.token);
      setInviteLink(result.inviteLink);
      setSuccess("Link gerado");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao gerar link");
    } finally {
      setLoading(false);
    }
  };

  // Revoke invite link
  const handleRevokeLink = async () => {
    if (!window.confirm("Revogar o link de convite atual?")) return;

    setLoading(true);
    try {
      await revokeInviteLink(conversation._id, user.token);
      setInviteLink("");
      setSuccess("Link revogado");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao revogar link");
    } finally {
      setLoading(false);
    }
  };

  // Copy invite link
  const handleCopyLink = () => {
    const fullLink = `${window.location.origin}/chat/join/${inviteLink}`;
    navigator.clipboard.writeText(fullLink);
    setSuccess("Link copiado!");
    setTimeout(() => setSuccess(""), 2000);
  };

  // Add members
  const handleAddMembers = async () => {
    if (selectedFriends.length === 0) return;

    setLoading(true);
    try {
      await addGroupMembers(
        conversation._id,
        selectedFriends.map((f) => f._id),
        user.token
      );
      setSuccess(`${selectedFriends.length} membro(s) adicionado(s)`);
      setSelectedFriends([]);
      setActiveSection("members");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao adicionar membros");
    } finally {
      setLoading(false);
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Remover este membro do grupo?")) return;

    setLoading(true);
    try {
      await removeGroupMember(conversation._id, memberId, user.token);
      setSuccess("Membro removido");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao remover membro");
    } finally {
      setLoading(false);
    }
  };

  // Make admin
  const handleMakeAdmin = async (memberId) => {
    setLoading(true);
    try {
      await makeGroupAdmin(conversation._id, memberId, user.token);
      setSuccess("Admin adicionado");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao adicionar admin");
    } finally {
      setLoading(false);
    }
  };

  // Remove admin
  const handleRemoveAdmin = async (memberId) => {
    setLoading(true);
    try {
      await removeGroupAdmin(conversation._id, memberId, user.token);
      setSuccess("Admin removido");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao remover admin");
    } finally {
      setLoading(false);
    }
  };

  // Leave group
  const handleLeaveGroup = async () => {
    if (!window.confirm("Sair do grupo? Você não poderá enviar mensagens.")) return;

    setLoading(true);
    try {
      await leaveGroup(conversation._id, user.token);
      onClose && onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao sair do grupo");
      setLoading(false);
    }
  };

  // Update settings
  const handleUpdateSettings = async () => {
    setLoading(true);
    try {
      await updateGroupSettings(conversation._id, settings, user.token);
      setSuccess("Configurações atualizadas");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao atualizar configurações");
    } finally {
      setLoading(false);
    }
  };

  // Toggle friend selection
  const toggleFriendSelection = (friend) => {
    setSelectedFriends((prev) => {
      const isSelected = prev.some((f) => f._id === friend._id);
      if (isSelected) {
        return prev.filter((f) => f._id !== friend._id);
      }
      return [...prev, friend];
    });
  };

  const filteredFriends = friends.filter((f) => {
    const name = `${f.first_name} ${f.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const getMemberRole = (memberId) => {
    if (conversation?.group?.creator === memberId) return "creator";
    if (conversation?.group?.admins?.includes(memberId)) return "admin";
    return "member";
  };

  return (
    <div className="group_info_panel">
      <div className="group_info_header">
        <button className="close_btn" onClick={onClose}>
          <CloseIcon />
        </button>
        <h3>Dados do grupo</h3>
      </div>

      {/* Messages */}
      {error && <div className="error_msg">{error}</div>}
      {success && <div className="success_msg">{success}</div>}

      {/* Navigation tabs */}
      <div className="group_info_tabs">
        <button
          className={activeSection === "info" ? "active" : ""}
          onClick={() => setActiveSection("info")}
        >
          Info
        </button>
        <button
          className={activeSection === "members" ? "active" : ""}
          onClick={() => setActiveSection("members")}
        >
          Membros ({conversation?.participants?.length || 0})
        </button>
        {isAdmin && (
          <button
            className={activeSection === "settings" ? "active" : ""}
            onClick={() => setActiveSection("settings")}
          >
            Config
          </button>
        )}
      </div>

      {/* Content sections */}
      <div className="group_info_content">
        {/* Info Section */}
        {activeSection === "info" && (
          <div className="info_section">
            {/* Group picture */}
            <div className="group_picture_container">
              <img
                src={conversation?.group?.picture}
                alt={conversation?.group?.name}
                onError={(e) => {
                  e.target.src =
                    "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                }}
              />
              {isAdmin && (
                <>
                  <button
                    className="edit_picture_btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CameraIcon color="#fff" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handlePictureChange}
                    hidden
                  />
                </>
              )}
            </div>

            {/* Group name */}
            <div className="info_item">
              <label>Nome do grupo</label>
              {editingName ? (
                <div className="edit_input">
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    maxLength={50}
                    autoFocus
                  />
                  <button onClick={handleUpdateName} disabled={loading}>
                    Salvar
                  </button>
                  <button onClick={() => setEditingName(false)}>Cancelar</button>
                </div>
              ) : (
                <div className="info_value">
                  <span>{conversation?.group?.name}</span>
                  {isAdmin && (
                    <button onClick={() => setEditingName(true)}>
                      <EditIcon />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Group description */}
            <div className="info_item">
              <label>Descrição</label>
              {editingDesc ? (
                <div className="edit_input">
                  <textarea
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    maxLength={200}
                    rows={3}
                  />
                  <button onClick={handleUpdateDescription} disabled={loading}>
                    Salvar
                  </button>
                  <button onClick={() => setEditingDesc(false)}>Cancelar</button>
                </div>
              ) : (
                <div className="info_value">
                  <span>
                    {conversation?.group?.description || "Sem descrição"}
                  </span>
                  {isAdmin && (
                    <button onClick={() => setEditingDesc(true)}>
                      <EditIcon />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Invite link section */}
            {isAdmin && (
              <div className="info_item invite_section">
                <label>
                  <LinkIcon /> Link de convite
                </label>
                {inviteLink ? (
                  <div className="invite_link_container">
                    <span className="invite_link">
                      {window.location.origin}/chat/join/{inviteLink}
                    </span>
                    <div className="invite_actions">
                      <button onClick={handleCopyLink} title="Copiar">
                        <CopyIcon />
                      </button>
                      <button
                        onClick={handleRevokeLink}
                        title="Revogar"
                        className="danger"
                      >
                        <DeleteIcon color="#e41e3f" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="generate_link_btn"
                    onClick={handleGenerateLink}
                    disabled={loading}
                  >
                    Gerar link de convite
                  </button>
                )}
              </div>
            )}

            {/* Created info */}
            <div className="info_item">
              <label>Criado em</label>
              <span className="info_value">
                {new Date(conversation?.createdAt).toLocaleDateString("pt-PT", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>

            {/* Leave group */}
            <button className="leave_group_btn" onClick={handleLeaveGroup}>
              <ExitIcon color="#e41e3f" />
              Sair do grupo
            </button>
          </div>
        )}

        {/* Members Section */}
        {activeSection === "members" && (
          <div className="members_section">
            {isAdmin && (
              <button
                className="add_members_btn"
                onClick={() => setActiveSection("addMembers")}
              >
                <AddPersonIcon color="#00a884" />
                Adicionar participantes
              </button>
            )}

            <div className="members_list">
              {conversation?.participants?.map((member) => {
                const role = getMemberRole(member._id);
                const isSelf = member._id === user?.id;

                return (
                  <div key={member._id} className="member_item">
                    <img
                      src={member.picture}
                      alt={member.first_name}
                      onError={(e) => {
                        e.target.src =
                          "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                      }}
                    />
                    <div className="member_info">
                      <span className="member_name">
                        {member.first_name} {member.last_name}
                        {isSelf && " (Você)"}
                      </span>
                      <span className="member_role">
                        {role === "creator" && "Criador do grupo"}
                        {role === "admin" && "Admin"}
                        {role === "member" && "@" + member.username}
                      </span>
                    </div>
                    {role !== "member" && (
                      <span className="admin_badge">
                        <AdminIcon color="#00a884" />
                      </span>
                    )}
                    {isAdmin && !isSelf && role !== "creator" && (
                      <div className="member_actions">
                        {role === "admin" ? (
                          <button
                            onClick={() => handleRemoveAdmin(member._id)}
                            title="Remover admin"
                          >
                            Remover admin
                          </button>
                        ) : (
                          <button
                            onClick={() => handleMakeAdmin(member._id)}
                            title="Tornar admin"
                          >
                            Tornar admin
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveMember(member._id)}
                          className="danger"
                          title="Remover"
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Members Section */}
        {activeSection === "addMembers" && (
          <div className="add_members_section">
            <div className="section_header">
              <button onClick={() => setActiveSection("members")}>
                ← Voltar
              </button>
              <h4>Adicionar participantes</h4>
            </div>

            {/* Search */}
            <div className="friends_search">
              <SearchIcon />
              <input
                type="text"
                placeholder="Pesquisar amigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Selected friends preview */}
            {selectedFriends.length > 0 && (
              <div className="selected_preview">
                {selectedFriends.map((f) => (
                  <div
                    key={f._id}
                    className="selected_friend"
                    onClick={() => toggleFriendSelection(f)}
                  >
                    <img
                      src={f.picture}
                      alt={f.first_name}
                      onError={(e) => {
                        e.target.src =
                          "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                      }}
                    />
                    <span className="remove">×</span>
                  </div>
                ))}
              </div>
            )}

            {/* Friends list */}
            <div className="friends_list">
              {filteredFriends.length === 0 ? (
                <p className="no_friends">Nenhum amigo disponível</p>
              ) : (
                filteredFriends.map((friend) => {
                  const isSelected = selectedFriends.some(
                    (f) => f._id === friend._id
                  );
                  return (
                    <div
                      key={friend._id}
                      className={`friend_item ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleFriendSelection(friend)}
                    >
                      <img
                        src={friend.picture}
                        alt={friend.first_name}
                        onError={(e) => {
                          e.target.src =
                            "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                        }}
                      />
                      <div className="friend_info">
                        <span>{friend.first_name} {friend.last_name}</span>
                        <span>@{friend.username}</span>
                      </div>
                      <div className={`checkbox ${isSelected ? "checked" : ""}`}>
                        {isSelected && "✓"}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Add button */}
            {selectedFriends.length > 0 && (
              <button
                className="confirm_add_btn"
                onClick={handleAddMembers}
                disabled={loading}
              >
                {loading ? (
                  <PulseLoader color="#fff" size={8} />
                ) : (
                  `Adicionar ${selectedFriends.length} participante(s)`
                )}
              </button>
            )}
          </div>
        )}

        {/* Settings Section */}
        {activeSection === "settings" && isAdmin && (
          <div className="settings_section">
            <div className="setting_item">
              <div className="setting_info">
                <span className="setting_title">
                  Apenas admins podem enviar mensagens
                </span>
                <span className="setting_desc">
                  Apenas administradores podem enviar mensagens no grupo
                </span>
              </div>
              <label className="toggle_switch">
                <input
                  type="checkbox"
                  checked={settings.onlyAdminsCanSend}
                  onChange={(e) =>
                    setSettings({ ...settings, onlyAdminsCanSend: e.target.checked })
                  }
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting_item">
              <div className="setting_info">
                <span className="setting_title">
                  Apenas admins podem editar info
                </span>
                <span className="setting_desc">
                  Apenas administradores podem editar nome e foto do grupo
                </span>
              </div>
              <label className="toggle_switch">
                <input
                  type="checkbox"
                  checked={settings.onlyAdminsCanEditInfo}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      onlyAdminsCanEditInfo: e.target.checked,
                    })
                  }
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting_item">
              <div className="setting_info">
                <span className="setting_title">Aprovar novos membros</span>
                <span className="setting_desc">
                  Admins precisam aprovar novos membros que entram via link
                </span>
              </div>
              <label className="toggle_switch">
                <input
                  type="checkbox"
                  checked={settings.approveNewMembers}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      approveNewMembers: e.target.checked,
                    })
                  }
                />
                <span className="slider"></span>
              </label>
            </div>

            <button
              className="save_settings_btn"
              onClick={handleUpdateSettings}
              disabled={loading}
            >
              {loading ? <PulseLoader color="#fff" size={8} /> : "Salvar configurações"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
